import {
  baseAcquisitionColumns,
  baseAddressTableColumns,
  baseAdvancePaymentRequestColumns,
  baseAgencyColumns,
  baseApplicationColumns,
  baseBusinessUnitColumns,
  baseCommercialConditionColumns,
  baseCompanyColumns,
  baseContactColumns,
  baseContractColumns,
  baseDepartmentColumns,
  baseDocumentColumns,
  baseEmailMessageColumns,
  baseExpenseClaimRequestColumns,
  baseFileColumns,
  baseHiringPoolColumns,
  baseMealCardRequestColumns,
  baseMedicalCenterColumns,
  baseMedicalCheckupRequestColumns,
  baseMissionColumns,
  baseMissionWorkspaceColumns,
  baseNoteColumns,
  baseNotificationColumns,
  baseOrganizationColumns,
  basePpeRequestColumns,
  baseProfessionalCardRequestColumns,
  baseProjectTypologyColumns,
  baseQualificationCenterColumns,
  baseQualificationCertificateColumns,
  baseRecruitmentOfficePaymentColumns,
  baseReferenceCheckColumns,
  baseRegionColumns,
  baseRemunerationProfileColumns,
  baseRemunerationTemplateColumns,
  baseResumeColumns,
  baseSafetyBookColumns,
  baseTalentColumns,
  baseTalentJobColumns,
  baseTalentJobExperienceColumns,
  baseTalentPushMarketingRequestColumns,
  baseTalentSoftwareColumns,
  baseTeamMemberColumns,
  baseWorkAccidentRequestColumns,
  baseWorkspaceColumns,
  baseDocumentInternalReviewColumns,
  baseDocumentExternalReviewColumns,
} from "./supabase.base-columns";

export const fileColumns = `
  ${baseFileColumns}
` as const;

export const companyColumns = `
  ${baseCompanyColumns},
  agencies:agency(
    id
  ),
  missions:mission(
    id
  ),
  contacts:contact(
    id
  ),
  billing_address:address_table(
    ${baseAddressTableColumns}
  )
` as const;

export const departmentColumns = `
  ${baseDepartmentColumns},
  region(
    ${baseRegionColumns},
    country(
      code,
      name
    )
  )
` as const;

export const addressTableListColumns = `
  ${baseAddressTableColumns},
  department(
    code,
    name,
    local_code,
    region(
      code,
      name
    )
  )
` as const;

export const agencyWithContractsColumns = `
  ${baseAgencyColumns},
  address:address_table(
    ${baseAddressTableColumns}
  ),
  contracts:contract(
    ${baseContractColumns}
  )
` as const;

// This is a composite part aimed to prevent timeout of 'agencyWithContractsColumns'
// It will be paired with 'baseContractColumns' to reconstruct a non timeout call
export const agencyMinimalInfosForCompanyColumns = `
  ${baseAgencyColumns},
  address:address_table(
    ${baseAddressTableColumns}
  ),
  contracts:contract(
    is_amendment
  )
` as const;

export const agencySearchColumns = `
  id,
  name,
  updated_at,
  company(
    name
  )
`;

export const authUserColumns = `
  id,
  first_name,
  last_name,
  email,
  phone_number,
  avatar_file:file!auth_user_avatar_file_id_fkey(
    ${baseFileColumns}
  ),
  created_at
` as const;

export const softwareColumns = `
  *
`;

export const talentSoftwareColumns = `
  ${baseTalentSoftwareColumns},
  software(
    ${softwareColumns}
  )
` as const;

export const teamMemberForAvatarColumns = `
  id,
  email,
  auth_user_id,
  workspace_id,
  main_workspace_id,
  management_level,
  offboarded_at,
  manager_id,
  business_unit!team_member_business_unit_id_fkey(
    emoji,
    name
  ),
  auth_user!team_member_auth_user_id_fkey(
    ${authUserColumns},
    user_role(
      role
    )
  )
` as const;

export const teamMemberResponsibleDuringAbsenceColumns = `
  team_member_taking_over:team_member!team_member_responsible_during__team_member_taking_over_id_fkey!inner(
    ${teamMemberForAvatarColumns}
  ),
  team_member_away_managees,
  previous_manager:team_member!team_member_responsible_during_absence_previous_manager_id_fkey!inner(
    ${teamMemberForAvatarColumns}
  )
` as const;

export const teamMemberColumns = `
  *,
  auth_user!team_member_auth_user_id_fkey(
    ${authUserColumns},
    user_role(
      role
    )
  ),
  current_workspace:workspace!team_member_workspace_id_fkey(
    ${baseWorkspaceColumns}
  ),
  main_workspace:workspace!public_team_member_main_workspace_id_fkey(
    ${baseWorkspaceColumns}
  ),
  team_member_workspaces:team_member_workspace!inner(
    ${baseTeamMemberColumns},
    workspace(
      ${baseWorkspaceColumns}
    )
  ),
  business_unit!team_member_business_unit_id_fkey(
    ${baseBusinessUnitColumns}
  ),
  regions:team_member_region(
    region(
      ${baseRegionColumns}
    )
  ),
  management_level,
  is_onboarded
` as const;

export const userRoleWithTeamMemberColumns = `
  *,
  auth_user!inner(
    ${authUserColumns},
    team_member!inner(
      ${teamMemberForAvatarColumns},
      is_independent
    )
  )
` as const;

export const workspaceColumns = `
  ${baseWorkspaceColumns},
  team_members:team_member!team_member_workspace_id_fkey(
    ${baseTeamMemberColumns}
  )
` as const;

export const contactColumns = `
  ${baseContactColumns},
  contact_full_name,
  company!inner(
    ${baseCompanyColumns}
  ),
  owner:team_member!contact_owner_id_fkey(
    ${teamMemberForAvatarColumns}
  )
` as const;

export const contactWithContractColumns = `
  ${contactColumns},
  contract_contact(
    contract_key,
    type
  ),
  agencies:contact_agency(
    agency(
      id,
      name,
      address:address_table(
        city,
        postal_code,
        department_code
      )
    )
  ),
  business_units:contact_business_unit(
    business_unit(
      id,
      name,
      emoji
    )
  )
` as const;

export const talentJobExperienceColumns = `
  ${baseTalentJobExperienceColumns}
` as const;

export const referenceCheckColumns = `
  ${baseReferenceCheckColumns},
  contact(
    id,
    first_name,
    last_name,
    email,
    phone_number,
    job_title,
    civility,
    company!inner(
      id,
      name
    ),
    owner:team_member!contact_owner_id_fkey(
      id,
      auth_user!inner(
        first_name,
        last_name,
        email
      )
    )
  ),
  talent!inner(
    ${baseTalentColumns},
    business_units:business_unit (
      ${baseBusinessUnitColumns}
    )
  ),
  created_by:auth_user!reference_check_created_by_fkey(
    ${authUserColumns},
    team_member(
      business_unit!team_member_business_unit_id_fkey(
        id
      )
    )
  ),
  updated_by:auth_user!reference_check_updated_by_fkey(
    ${authUserColumns}
  )
` as const;

export const referenceCheckForTableColumns = `
  ${baseReferenceCheckColumns},
  contact(
    id,
    first_name,
    last_name
  ),
  talent!inner(
    id,
    first_name,
    last_name
  ),
  created_by:auth_user!reference_check_created_by_fkey(
    id,
    first_name,
    last_name,
    team_member(
      ${teamMemberForAvatarColumns}
    )
  )
` as const;

export const hiringPoolColumns = `
  ${baseHiringPoolColumns},
  business_unit(
    ${baseBusinessUnitColumns}
  )
` as const;

export const talentForRowColumns = `
  ${baseTalentColumns},
  resume(
    ${baseResumeColumns},
    file:file!resume_file_id_fkey(
      ${baseFileColumns}
    )
  ),
  hiring_pool(
    ${hiringPoolColumns}
  ),
  business_units:business_unit (
    ${baseBusinessUnitColumns}
  ),
  application(
    id,
    stage,
    is_rejected,
    mission_id
  ),
  created_by_user:auth_user!talent_created_by_fkey(
    ${authUserColumns}
  ),
  talent_workspaces:talent_workspace(
    *
  ),
  address_table(
    ${baseAddressTableColumns},
    department(
      code,
      name,
      local_code,
      region(
        code,
        name
      )
    )
  )
` as const;

export const fullTalentColumns = `
  ${baseTalentColumns},
  address_table(
    ${baseAddressTableColumns},
    department(
      code,
      name,
      local_code,
      region(
        code,
        name
      )
    )
  )
` as const;

export const talentColumns = `
  ${talentForRowColumns},
  document(
    qualification_certificate(
      ${baseQualificationCertificateColumns}
    )
  ),
  reference_check(
    ${baseReferenceCheckColumns}
  ),
  talent_job_experience(
    ${baseTalentJobExperienceColumns}
  ),
  talent_job(
    ${baseTalentJobColumns}
  ),
  talent_project_typology(
    project_typology(
      ${baseProjectTypologyColumns}
    )
  ),
  talent_software(
    ${baseTalentSoftwareColumns}
  ),
  updated_by_user:auth_user!talent_updated_by_fkey(
    ${authUserColumns}
  ),
  advance_payment_status_updater:auth_user!talent_block_advance_payment_updated_by_fkey(
    ${authUserColumns}
  ),
  acquisition(
    ${baseAcquisitionColumns}
  ),
  owner:team_member!talent_owner_id_fkey(
    ${teamMemberForAvatarColumns}
  ),
  profile_picture:file!talent_profile_picture_id_fkey(
    ${baseFileColumns}
  ),
  talent_resume_data(
    parsed_text,
    standardized_text
  )
` as const;

export const talentSearchColumns = `
  id,
  first_name,
  last_name,
  email,
  phone_number,
  public_id,
  updated_at,
  created_at,
  talent_workspace!inner(*)
`;

export const companyDetailColumns = `
  ${baseCompanyColumns},
  billing_address:address_table(
    ${baseAddressTableColumns}
  ),
  risk_updated_by:team_member(
    ${teamMemberForAvatarColumns}
  )
` as const;

export const agencyColumns = `
  ${baseAgencyColumns},
  address:address_table(
    ${baseAddressTableColumns}
  ),
  company(
    ${baseCompanyColumns}
  ),
  contracts:contract(
    id
  ),
  missions:mission(
    id
  )
` as const;

export const contractColumns = `
  ${baseContractColumns},
  timesheet_transmission_type,
  talent!inner(
    id,
    has_auth_access,
    first_name,
    last_name,
    email,
    phone_number,
    block_advance_payment_reason,
    emergency_contact,
    address_table(
      ${baseAddressTableColumns}
    ),
    birth_date
  ),
  job(
    id,
    name
  ),
  mission!inner(
    id,
    status,
    address:address_table(
      ${baseAddressTableColumns}
    ),
    company(
      name
    ),
    contracts:contract(
      id
    )
  ),
  agency!inner(
    ${agencyColumns}
  ),
  owner:team_member!contract_owner_id_fkey(
    ${teamMemberForAvatarColumns}
  ),
  admin:team_member!contract_admin_id_fkey(
   id
  ),
  created_by:auth_user!contract_created_by_fkey(
    first_name,
    last_name
  ),
  created_by_team_member:auth_user!contract_created_by_fkey(
    team_member(
        ${teamMemberForAvatarColumns}
    )
  ),
  updated_by:auth_user!contract_updated_by_fkey(
    first_name,
    last_name
  ),
  address:address_table(
    ${baseAddressTableColumns}
  ),
  organization!contract_organization_id_fkey(
    ${baseOrganizationColumns}
  ),
  validator:team_member!contract_validated_by_fkey(
    ${teamMemberForAvatarColumns}
  )
` as const;

/**
 * Shared by three endpoints — check them all before changing it:
 * - GET /contracts/:contractId and GET /contracts/planning (`contractDetailSchema`)
 * - GET /contracts, the list hydrate (`contractSchema`)
 *
 * Drops the agency-wide contract and mission id lists and the mission contract ids that contractColumns
 * embeds for the agency picker — embedding those is what made the pages slow. The schemas answer them
 * as empty lists.
 */
export const contractDetailColumns = `
  ${baseContractColumns},
  timesheet_transmission_type,
  talent!inner(
    id,
    has_auth_access,
    first_name,
    last_name,
    email,
    phone_number,
    block_advance_payment_reason,
    emergency_contact,
    address_table(
      ${baseAddressTableColumns}
    ),
    birth_date
  ),
  job(
    id,
    name
  ),
  mission!inner(
    id,
    status,
    address:address_table(
      ${baseAddressTableColumns}
    ),
    company(
      name
    )
  ),
  agency!inner(
    ${baseAgencyColumns},
    address:address_table(
      ${baseAddressTableColumns}
    ),
    company(
      ${baseCompanyColumns}
    )
  ),
  owner:team_member!contract_owner_id_fkey(
    ${teamMemberForAvatarColumns}
  ),
  admin:team_member!contract_admin_id_fkey(
   id
  ),
  created_by:auth_user!contract_created_by_fkey(
    first_name,
    last_name
  ),
  created_by_team_member:auth_user!contract_created_by_fkey(
    team_member(
        ${teamMemberForAvatarColumns}
    )
  ),
  updated_by:auth_user!contract_updated_by_fkey(
    first_name,
    last_name
  ),
  address:address_table(
    ${baseAddressTableColumns}
  ),
  organization!contract_organization_id_fkey(
    ${baseOrganizationColumns}
  ),
  validator:team_member!contract_validated_by_fkey(
    ${teamMemberForAvatarColumns}
  )
` as const;

export const contractForHistoryColumns = `
  id,
  contract_key,
  job_name,
  amendment_type,
  start_date,
  end_date,
  real_end_date,
  status,
  owner_id,
  workspace_id,
  created_at
`;

export const contractForDefinitiveEndColumns = `
  id,
  is_definitive_end,
  real_end_date,
  is_termination_bonus_to_be_paid,
  definitive_end_type,
  definitive_end_details,
  definitive_end_processed_at,
  is_definitive_end_processed,
  definitive_end_created_at,
  talent!inner(
    id,
    first_name,
    last_name
  ),
  created_by:auth_user!contract_created_by_fkey(
    first_name,
    last_name
  ),
  owner:team_member!contract_owner_id_fkey!inner(
    ${teamMemberForAvatarColumns},
    business_unit_id
  ),
  agency(
    name
  )
` as const;

export const missionColumns = `
  ${baseMissionColumns},
  application(
    id,
    stage,
    talent_id,
    is_rejected,
    has_stage_been_forced
  ),
  address:address_table(
    ${baseAddressTableColumns}
  ),
  company(
    ${baseCompanyColumns}
  ),
  agency(
    ${baseAgencyColumns}
  ),
  business_unit(
    ${baseBusinessUnitColumns}
  ),
  project_typology(
    ${baseProjectTypologyColumns}
  ),
  software(
    ${softwareColumns}
  ),
  qualification_certificate(
    ${baseQualificationCertificateColumns}
  ),
  owner:team_member!mission_owner_id_fkey(
    ${teamMemberForAvatarColumns}
  ),
  assignee:team_member!mission_assignee_id_fkey(
    ${teamMemberForAvatarColumns}
  ),
  lead_contact:contact!mission_lead_contact_id_fkey(
    ${contactColumns}
  ),
  interviewer_contact:contact!mission_interviewer_contact_id_fkey(
    ${contactColumns}
  ),
  decision_maker_contact:contact!mission_decision_maker_contact_id_fkey(
    ${contactColumns}
  ),
  workspace!mission_workspace_id_fkey(
    name
  ),
  updated_by:auth_user!mission_updated_by_fkey(
    ${authUserColumns}
  ),
  created_by:auth_user!mission_created_by_fkey(
    ${authUserColumns}
  ),
  mission_workspaces:mission_workspace!inner(
    ${baseMissionWorkspaceColumns}
  )
` as const;

export const missionForTalentSourcingColumns = `
  id,
  job_name,
  contract_types,
  experience_level_researched,
  is_driver_license_required,
  is_long_business_trips_required,
  address:address_table(
    ${baseAddressTableColumns}
  )
` as const;

export const missionForRowColumns = `
  id,
  public_id,
  created_at,
  created_by:auth_user!mission_created_by_fkey(
    ${authUserColumns}
  ),
  priority,
  status,
  job_name,
  start_date,
  job_extra_info,
  number_of_open_positions,
  job_posting_status,
  job_posting_date,
  workspace_id,
  is_public,
  is_client_order,
  closing_reason,
  company(
    name
  ),
  business_unit(
    emoji,
    name
  ),
  address:address_table(
    ${baseAddressTableColumns}
  ),
  owner:team_member!mission_owner_id_fkey(
    ${teamMemberForAvatarColumns}
  ),
  assignee:team_member!mission_assignee_id_fkey(
    ${teamMemberForAvatarColumns}
  ),
  application(
    id,
    stage,
    is_rejected
  ),
  mission_workspaces:mission_workspace!inner(
    workspace_id
  )
` as const;

export const missionColumnsForApplication = `
    id,
    job_name,
    start_date,
    end_date,
    decision_maker_contact_id,
    job_id,
    company_id,
    working_days,
    working_hours,
    employee_status,
    coefficient,
    workspace_id,
    business_unit_id,
    is_public,
    is_client_order,
    company(
     ${baseCompanyColumns}
    ),
    job(
      name
    ),
    lead_contact:contact!mission_lead_contact_id_fkey(
      ${contactColumns}
    ),
    address:address_table(
      ${baseAddressTableColumns}
    ),
    owner:team_member!mission_owner_id_fkey(
      ${teamMemberForAvatarColumns}
    ),
    assignee:team_member!mission_assignee_id_fkey(
      ${teamMemberForAvatarColumns}
    ),
    mission_workspaces:mission_workspace!inner (
      workspace_id
    )
` as const;

export const applicationColumns = `
  ${baseApplicationColumns},
  mission!inner(
    ${missionColumnsForApplication}
  ),
  talent!inner(
    id,
    first_name,
    last_name,
    email,
    phone_number,
    job_name,
    address_table(
      ${baseAddressTableColumns}
    ),
    experience_level,
    has_driver_license,
    salary_expectation,
    created_by_user:auth_user!talent_created_by_fkey(
      ${authUserColumns}
    ),
    owner:team_member!talent_owner_id_fkey(
      ${teamMemberForAvatarColumns}
    ),
    hiring_pools:talent_hiring_pool(
      hiring_pool(
        ${hiringPoolColumns}
      )
    ),
    talent_workspaces:talent_workspace!inner (
      workspace_id
    )
  ),
  created_by_team_member:auth_user!application_created_by_fkey(
    team_member!team_member_auth_user_id_fkey(
        ${teamMemberForAvatarColumns}
    )
  ),
  updated_by_team_member:auth_user!application_updated_by_fkey(
    team_member!team_member_auth_user_id_fkey(
        ${teamMemberForAvatarColumns}
    )
  ),
  application_emails:application_email_message(
    id,
    created_at
  ),
  rejected_by:auth_user!application_rejected_by_fkey(
    team_member(
      ${teamMemberForAvatarColumns}
    )
  ),
  rejected_at
` as const;

export const qualificationCertificateColumns = `
  ${baseQualificationCertificateColumns}
` as const;

export const qualificationTrainingRequestForTableColumns = `
  id,
  status,
  created_at,
  start_at,
  end_at,
  near_to,
  to_reschedule,
  price,
  request_comment,
  transfer_comment,
  possible_start_date,
  possible_end_date,

  contract_id,
  talent_availability,
  other_qualification_certificates,
  is_recycling,
  is_invoiceable_to_agency,
  has_meal_voucher,
  is_paid_during_training,
  cancellation_comment,
  cancellation_reason,
  cancelled_at,
  workspace_id,

  auth_user(
    first_name,
    last_name
  ),
  talent(
    id,
    first_name,
    last_name,
    email
  ),
  qualification_center(
    id,
    name
  ),
  qualification_certificate(
    id,
    created_at,
    name,
    workspace_id
  ),
  wsm:team_member!qualification_training_request_wsm_id_fkey(
    ${teamMemberForAvatarColumns}
  ),
  owner:team_member!qualification_training_request_owner_id_fkey!inner(
    ${teamMemberForAvatarColumns},
    business_unit_id
  ),
  cancelled_by:team_member!qualification_training_request_cancelled_by_fkey(
    ${teamMemberForAvatarColumns},
    business_unit_id
  )
` as const;

export const qualificationCenterColumns = `
  ${baseQualificationCenterColumns},
  qualification_center_certificate(
    qualification_certificate_id
  )
` as const;

export const independentPaymentRequestColumns = `
  id,
  month,
  amount,
  comment,
  status,
  independent:team_member!inner(
    ${teamMemberForAvatarColumns}
  ),
  file(
    ${baseFileColumns}
  )
` as const;

export const documentFilesColumns = `
  files:document_file(
    file(
      ${baseFileColumns}
    )
  ),
  expiration_date,
  docuseal_submission_url,
  docuseal_submission_id,
  docuseal_template_id
` as const;

export const documentColumns = `
  ${baseDocumentColumns},
  ${documentFilesColumns}
` as const;

export const documentReviewColumns = `
  internal_reviews:document_internal_review(
    ${baseDocumentInternalReviewColumns},
    reviewer:auth_user!document_internal_review_created_by_fkey(
      first_name,
      last_name
    )
  ),
  external_reviews:document_external_review(
    ${baseDocumentExternalReviewColumns}
  )
` as const;

export const fullDocumentColumns = `
  ${documentColumns},
  creator:auth_user!document_created_by_fkey(
    id,
    email,
    first_name,
    last_name,
    team_member!team_member_auth_user_id_fkey(
      id
    )
  ),
  ${documentReviewColumns}
` as const;

export const talentDocumentColumns = `
  ${fullDocumentColumns},
  contract_documents:contract_document(
    type,
    contract(
      contract_key,
      start_date,
      end_date,
      status
    )
  )
` as const;

export const recruitmentOfficeInvoiceRequestForTableColumns = `
  id,
  status,
  annual_wage,
  job_name,
  company!inner(
    id,
    name
  ),
  talent!inner(
    id,
    first_name,
    last_name
  ),
  owner:team_member!inner(
    ${teamMemberColumns}
  ),
  created_at,
  fee_amount,
  is_fee_percentage
` as const;

export const recruitmentOfficeInvoiceRequestColumns = `
  id,
  status,
  annual_wage,
  fee_comment,
  is_fee_percentage,
  fee_amount,
  start_date,
  comment,
  timeline,
  job_name,
  work_order_id,
  company!inner(
    ${companyColumns}
  ),
  talent!inner(
    ${baseTalentColumns}
  ),
  owner:team_member!inner(
    ${teamMemberForAvatarColumns}
  ),
  created_at
` as const;

export const recruitmentOfficePaymentColumns = `
  ${baseRecruitmentOfficePaymentColumns},
  recruitment_office_invoice_request!inner(
    ${recruitmentOfficeInvoiceRequestColumns}
  )
` as const;

export const medicalCheckupRequestColumns = `
  ${baseMedicalCheckupRequestColumns},
  medical_center(
    id,
    name,
    address
  ),
  auth_user(
    first_name,
    last_name
  ),
  wsm:team_member!medical_checkup_request_wsm_id_fkey(
    ${teamMemberForAvatarColumns}
  ),
  owner:team_member!medical_checkup_request_owner_id_fkey!inner(
    ${teamMemberForAvatarColumns},
    business_unit_id
  ),
  contract!inner(
    id,
    talent(
      id,
      first_name,
      last_name
    ),
    agency!inner(
      id,
      name
    )
  )
` as const;

export const medicalCheckupRequestForTableColumns = `
  ${baseMedicalCheckupRequestColumns},
  auth_user(
    id,
    first_name,
    last_name
  ),
  medical_center(
    id,
    name,
    address
  ),
  talent!inner(
    id,
    first_name,
    last_name
  ),
  contract!inner(
    id,
    start_date,
    end_date,
    status,
    agency!inner(
      id,
      name
    ),
    address_table(
      postal_code,
      city,
      region:region_name
    )
  ),
  owner:team_member!medical_checkup_request_owner_id_fkey!inner(
    ${teamMemberForAvatarColumns},
    business_unit_id
  )
` as const;

export const medicalCenterColumns = `
  ${baseMedicalCenterColumns}
` as const;

export const talentForProfessionalCardRequestColumns = `
  id,
  first_name,
  last_name,
  email,
  nationality,
  birth_date,
  gender,
  city_of_birth,
  country_of_birth,
  residency_permit_number,
  picture_id,
  professional_card_number
`;

export const professionalCardRequestColumns = `
  ${baseProfessionalCardRequestColumns},
  talent!inner(
    ${talentForProfessionalCardRequestColumns}
  ),
  auth_user!professional_card_request_created_by_fkey(
    first_name,
    last_name
  ),
  owner:team_member!professional_card_request_owner_id_fkey!inner(
    ${teamMemberForAvatarColumns},
    business_unit_id
  ),
  refused_by_team_member:team_member!professional_card_request_refused_by_fkey(
    id,
    auth_user!team_member_auth_user_id_fkey(
      first_name,
      last_name
    )
  ),
  contract(
    id,
    start_date,
    end_date,
    status,
    is_professional_card_required
  )
` as const;

export const expenseClaimRequestColumns = `
  ${baseExpenseClaimRequestColumns},
  auth_user(
    first_name,
    last_name,
    email
  ),
  talent!inner(
    id,
    first_name,
    last_name,
    email
  ),
  owner:team_member!expense_claim_request_owner_id_fkey(
    ${teamMemberForAvatarColumns}
  ),
  validator:team_member!expense_claim_request_validated_by_fkey(
    ${teamMemberForAvatarColumns}
  )
` as const;

export const advancePaymentRequestColumns = `
  ${baseAdvancePaymentRequestColumns},
  auth_user(
    first_name,
    last_name
  ),
  talent!inner(
    id,
    first_name,
    last_name,
    email,
    block_advance_payment_reason,
    block_advance_payment_updated_at,
    advance_payment_status_updater:auth_user!talent_block_advance_payment_updated_by_fkey(
      id,
      first_name,
      last_name
    )
  ),
  contract(
    id,
    agency!inner(
      id,
      name,
      company(
        name
      )
    ),
    organization(
      ${baseOrganizationColumns}
    ),
    is_sirh_request,
    sirh_name,
    timesheet_transmission_type
  ),
  owner:team_member!advance_payment_request_owner_id_fkey(
    ${teamMemberForAvatarColumns}
  )
` as const;

export const ppeRequestColumns = `
  ${basePpeRequestColumns},
  auth_user!ppe_request_created_by_fkey(
    first_name,
    last_name
  ),
  contract!inner(
    id,
    talent!inner(
      id,
      first_name,
      last_name,
      email
    )
  ),
  wsm:team_member!ppe_request_wsm_id_fkey(
    ${teamMemberForAvatarColumns}
  ),
  owner:team_member!ppe_request_owner_id_fkey!inner(
    ${teamMemberForAvatarColumns},
    business_unit_id
  ),
  ppes:ppe_request_item!ppe_request_item_request_id_fkey(
    id,
    attribute,
    equipment:ppe_item!inner(
     name:equipment
    ),
    is_canceled
  )
` as const;

export const noteColumns = `
  ${baseNoteColumns},
  updated_by:auth_user!note_updated_by_fkey(
    ${authUserColumns}
  ),
  created_by:auth_user!note_created_by_fkey(
    ${authUserColumns}
  )
` as const;

export const notificationColumns = `
  ${baseNotificationColumns}
` as const;

export const contractForTimesheetColumns = `
  id,
  contract_key,
  start_date,
  end_date,
  job_name,
  timesheet_transmission_type,
  talent!inner(
    id,
    first_name,
    last_name,
    email,
    phone_number
  ),
  agency!inner(
    name,
    company!inner(
      name
    )
  ),
  organization!inner(
    id,
    name,
    address_table(
      street1,
      street2,
      postal_code,
      city
    )
  )
`;

export const contractForTimesheetLinkColumns = `
  start_date,
  status,
  timesheet_transmission_type,
  organization_id,
  owner:team_member!contract_owner_id_fkey!inner(
    email,
    auth_user!team_member_auth_user_id_fkey(
      first_name,
      last_name,
      phone_number
    )
  ),
  talent(
    first_name,
    last_name
  )
` as const;

export const commercialConditionColumns = `
  ${baseCommercialConditionColumns},
  company(
    id,
    name
  ),
  auth_user!commercial_condition_created_by_fkey!inner(
    id,
    team_member!inner(
      id,
      ${teamMemberForAvatarColumns}
    )
  ),
  generated_file:file!commercial_condition_generated_file_id_fkey(
    ${baseFileColumns}
  ),
  signed_file:file!commercial_condition_signed_file_id_fkey(
    ${baseFileColumns}
  ),
  created_by:auth_user!commercial_condition_created_by_fkey(
    ${authUserColumns}
  ),
  business_unit(
    ${baseBusinessUnitColumns}
  ),
  docuseal_template_id,
  docuseal_submission_id,
  docuseal_submission_url,
  docuseal_sent_by,
  docuseal_is_archived
` as const;

export const timesheetWeekColumns = `
  status,
  contract_key,
  date
`;

export const companyInsuranceRequestColumns = `*,
  company(
    name
  ),
  owner:team_member(
    ${teamMemberForAvatarColumns}
  )
` as const;

export const userForTableColumns = `
  ${authUserColumns},
  user_roles:user_role(
    role
  ),
  team_member!inner(
    ${teamMemberForAvatarColumns}
  )
` as const;

export const smsColumns = `
  id,
  talent_id,
  send_at,
  content,
  is_sent,
  auth_user(
    team_member (
      ${teamMemberForAvatarColumns}
    )
  ),
  canceled_at
` as const;

export const workAccidentRequestColumns = `
  ${baseWorkAccidentRequestColumns},
  contract!inner(
    start_date,
    end_date,
    id,
    contract_key,
    job_name,
    talent(
      id,
      first_name,
      last_name,
      has_auth_access
    ),
    agency!inner(
      id,
      name,
      company!inner(
        id,
        name
      )
    ),
    organization(
      ${baseOrganizationColumns}
    )
  ),
  treated_by:team_member!work_accident_request_treated_by_fkey(
    ${teamMemberForAvatarColumns}
  ),
  owner:team_member!work_accident_request_owner_id_fkey(
    ${teamMemberForAvatarColumns}
  )
` as const;

export const emailMessageColumns = `
  ${baseEmailMessageColumns},
  owner:team_member!email_message_owner_id_fkey(${teamMemberForAvatarColumns}
  ),
  auth_user(
    team_member (
      ${teamMemberForAvatarColumns}
    )
  )
` as const;

export const emailTemplateColumns = `*`;

export const regionColumns = `
  ${baseRegionColumns}
` as const;

export const talentPushMarketingRequestColumns = `
  ${baseTalentPushMarketingRequestColumns},
  created_by_team_member:auth_user!talent_push_marketing_request_created_by_fkey(
    team_member(
      ${teamMemberForAvatarColumns}
    )
  ),
  updated_by_team_member:auth_user!talent_push_marketing_request_updated_by_fkey(
    team_member(
      ${teamMemberForAvatarColumns}
    )
  )
` as const;

export const talentPushMarketingRequestWithTalentColumns = `
  ${baseTalentPushMarketingRequestColumns},
  talent(
          first_name,
          last_name,
          job_name,
          address_table(
            department(
              name,
              local_code,
              region(name)
      )
    )
  )
` as const;

export const mealCardRequestColumns = `
  ${baseMealCardRequestColumns},
  contract(
    owner:team_member!contract_owner_id_fkey(
    ${teamMemberForAvatarColumns}
  ),
    meal_card_value
  ),
  talent:talent!meal_card_request_talent_id_fkey(
    ${baseTalentColumns},
    address_table(
      street1,
      street2,
      postal_code,
      city
    )
  )
` as const;

export const safetyBookColumns = `
  ${baseSafetyBookColumns},
  file:file!safety_book_file_id_fkey(
    ${baseFileColumns}
  )
` as const;

export const remunerationProfileColumns = `
  ${baseRemunerationProfileColumns}
` as const;

export const remunerationTemplateColumns = `
  ${baseRemunerationTemplateColumns},
  remuneration_profile:remuneration_profile!remuneration_template_remuneration_profile_id_fkey(
    ${remunerationProfileColumns}
  )
` as const;
